/**
 * Relewise card interactions.
 *
 * The Relewise Shopify app renders product cards inside the shadow DOM of
 * <relewise-product-tile> (and similar) web components. This script runs on
 * the parent page (loaded from layout/theme.liquid) and bridges across the
 * shadow boundary for two purposes:
 *
 *   1. Swatch swap — clicks on .tabbed-product-card__swatch-link inside a
 *      shadow root navigate to the variant via composedPath() bubbling.
 *      We intercept, fetch the variant product JSON, and update the card's
 *      image / title / price / link / sizes in place.
 *
 *   2. Hide Relewise's native favorite button — we already render our own
 *      .wishlist-heart in the card snippet. Without this, two heart icons
 *      stack in the top-right corner. We inject a `display: none` stylesheet
 *      into every Relewise shadow root we encounter.
 *
 * To reach inside the shadow DOMs we walk recursively (querySelectorAll does
 * not pierce shadow boundaries) and attach a MutationObserver to each shadow
 * root so cards inserted later get the same treatment.
 */
(function () {
  'use strict';

  /* ---------- shared helpers ---------- */

  var productCache = {};

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function formatMoney(cents) {
    var sh = window.Shopify && window.Shopify.currency;
    var active = (sh && sh.active) || 'SEK';
    var locale = (document.documentElement.lang || 'sv-SE').replace('_', '-');
    var amount = cents / 100;
    try {
      // Strip a trailing "." some locales append to the currency
      // abbreviation (e.g. Danish "kr.") so it matches Shopify's
      // `| money` render ("kr"). Prices always end in two decimal
      // digits, so a trailing dot is never the decimal separator.
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: active,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount).replace(/\.$/, '');
    } catch (_) {
      return amount.toFixed(2) + ' ' + active;
    }
  }

  function renderPriceHtml(data) {
    var html = '';
    if (data.compare_at_price && data.compare_at_price > data.price) {
      html += '<span class="tabbed-product-card__price--compare">' +
        formatMoney(data.compare_at_price) + '</span>';
    }
    html += formatMoney(data.price);
    return html;
  }

  function sizedUrl(url, width) {
    if (typeof url !== 'string') return url;
    if (url.indexOf('_x.') !== -1) return url.replace('_x.', '_' + width + 'x.');
    return url.replace(/(\.[a-z]{3,4})(\?|$)/, '_' + width + 'x$1$2');
  }

  function buildSrcset(url) {
    return sizedUrl(url, 360) + ' 360w, ' +
      sizedUrl(url, 533) + ' 533w, ' +
      sizedUrl(url, 720) + ' 720w, ' +
      sizedUrl(url, 940) + ' 940w';
  }

  /* ---------- swatch swap ---------- */

  document.addEventListener('click', onSwatchClick, true);

  function onSwatchClick(event) {
    var path = event.composedPath();
    var swatchLink = null;
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.matches && el.matches('.tabbed-product-card__swatch-link[data-product-handle]')) {
        swatchLink = el;
        break;
      }
    }
    if (!swatchLink) return;

    // Skip clicks outside shadow DOM — the section-level listener handles those.
    var root = swatchLink.getRootNode();
    var isInShadow = root && typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot;
    if (!isInShadow) return;

    event.preventDefault();
    event.stopPropagation();

    var card = swatchLink.closest('.tabbed-product-card');
    if (!card) return;

    var handle = swatchLink.getAttribute('data-product-handle');
    var productUrl = swatchLink.getAttribute('data-product-url');
    if (!handle) return;

    card.querySelectorAll('.tabbed-product-card__swatch').forEach(function (s) {
      s.classList.remove('tabbed-product-card__swatch--active');
    });
    var swatch = swatchLink.querySelector('.tabbed-product-card__swatch');
    if (swatch) swatch.classList.add('tabbed-product-card__swatch--active');

    // Outlet badge tracks the selected color's color_type metaobject value.
    applyOutletBadge(card, swatchLink.getAttribute('data-color-type'));

    fetchProduct(handle).then(function (data) {
      if (!data) return;
      applyVariant(card, data, productUrl);
    });
  }

  /* ---------- size pill → add to cart ----------
     The card snippet renders each size as a
     <button.tabbed-product-card__size-btn data-variant-id> that already
     carries the resolved variant id and an "Add <size> to cart" aria-label.
     But the button sits inside the card's wrapping <a>, so without this a
     click just bubbles to the anchor and navigates to the PDP. We intercept
     in the capture phase, POST the variant to the cart, and slide the cart
     drawer open — the theme's standard add-to-cart feedback. Scoped to
     clicks inside a Relewise shadow root; light-DOM cards are handled by
     their own section markup. */

  document.addEventListener('click', onSizeClick, true);

  function onSizeClick(event) {
    var path = event.composedPath();
    var btn = null;
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.matches && el.matches('.tabbed-product-card__size-btn[data-variant-id]')) {
        btn = el;
        break;
      }
    }
    if (!btn) return;

    var root = btn.getRootNode();
    var isInShadow = root && typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot;
    if (!isInShadow) return;

    // Always stop the click from reaching the wrapping <a> (PDP navigation),
    // even for unavailable sizes.
    event.preventDefault();
    event.stopPropagation();

    if (btn.disabled ||
        btn.classList.contains('tabbed-product-card__size-btn--unavailable') ||
        btn.__rwAdding) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    addVariantToCart(btn, variantId);
  }

  function addVariantToCart(btn, variantId) {
    var cart = document.querySelector('cart-drawer') ||
               document.querySelector('cart-notification');

    btn.__rwAdding = true;
    btn.classList.add('tabbed-product-card__size-btn--loading');

    // Single-item form (id + quantity) so the response is the line item
    // object with a top-level `id` + `sections`, the shape renderContents reads.
    var body = { id: Number(variantId), quantity: 1 };
    if (cart && cart.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
      if (cart.setActiveElement) cart.setActiveElement(btn);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        // response.status is set on errors (e.g. sold out / inventory).
        if (response && response.status) return;

        if (typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'relewise-size-btn',
            productVariantId: variantId,
            cartData: response
          });
        }

        if (cart && cart.renderContents) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);
        } else {
          // No drawer on this page — fall back to the cart page.
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(function () { /* swallow — keep the grid intact */ })
      .finally(function () {
        btn.__rwAdding = false;
        btn.classList.remove('tabbed-product-card__size-btn--loading');
      });
  }

  function applyVariant(card, data, productUrl) {
    var primaryImg = card.querySelector('.tabbed-product-card__img--primary');
    var hoverImg = card.querySelector('.tabbed-product-card__img--hover');

    if (primaryImg && data.images && data.images[0]) {
      primaryImg.src = sizedUrl(data.images[0], 720);
      primaryImg.srcset = buildSrcset(data.images[0]);
    }

    if (data.images && data.images[1]) {
      if (!hoverImg) {
        hoverImg = document.createElement('img');
        hoverImg.className = 'tabbed-product-card__img tabbed-product-card__img--hover';
        hoverImg.loading = 'lazy';
        hoverImg.sizes = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
        var media = card.querySelector('.tabbed-product-card__media');
        if (media) media.appendChild(hoverImg);
      }
      hoverImg.src = sizedUrl(data.images[1], 720);
      hoverImg.srcset = buildSrcset(data.images[1]);
    } else if (hoverImg) {
      hoverImg.remove();
    }

    var mainLink = card.querySelector('.tabbed-product-card__link');
    var titleLink = card.querySelector('.tabbed-product-card__title-link');
    if (productUrl) {
      if (mainLink) mainLink.href = productUrl;
      if (titleLink) titleLink.href = productUrl;
    }

    var titleEl = card.querySelector('.tabbed-product-card__title');
    if (titleEl) titleEl.textContent = data.title;

    var priceEl = card.querySelector('.tabbed-product-card__price');
    if (priceEl) priceEl.innerHTML = renderPriceHtml(data);

    var sizesContainer = card.querySelector('.tabbed-product-card__sizes');
    if (sizesContainer && data.options && data.variants) {
      var sizeOptionIndex = -1;
      data.options.forEach(function (opt, i) {
        // /products/{handle}.js returns options as objects
        // ({name, position, values}) on this store, not plain strings —
        // read .name when present so we still find the Size option.
        var optName = opt && typeof opt === 'object' ? opt.name : opt;
        if (String(optName).toLowerCase() === 'size') sizeOptionIndex = i;
      });
      if (sizeOptionIndex > -1) {
        var seenSizes = {};
        var html = '';
        data.variants.forEach(function (v) {
          var sizeVal = v.options[sizeOptionIndex];
          if (seenSizes[sizeVal]) return;
          seenSizes[sizeVal] = true;
          var available = data.variants.some(function (vv) {
            return vv.options[sizeOptionIndex] === sizeVal && vv.available;
          });
          var variantId = available
            ? data.variants.find(function (vv) { return vv.options[sizeOptionIndex] === sizeVal && vv.available; }).id
            : v.id;
          html += '<button type="button" class="tabbed-product-card__size-btn' +
            (available ? '' : ' tabbed-product-card__size-btn--unavailable') + '"' +
            ' data-variant-id="' + variantId + '"' +
            ' data-size-label="' + sizeVal + '"' +
            (available ? '' : ' disabled') +
            ' aria-label="Add ' + sizeVal + ' to cart">' +
            sizeVal + '</button>';
        });
        sizesContainer.innerHTML = html;
      }
    }
  }

  /* Show/hide the top-left "Outlet" badge to match the selected color's
     color_type metaobject value. Server-side render seeds the initial state;
     this keeps it correct after an in-place swatch swap. */
  function applyOutletBadge(card, colorType) {
    var isOutlet = String(colorType || '').toLowerCase() === 'outlet';
    var media = card.querySelector('.tabbed-product-card__media');
    if (!media) return;

    var container = media.querySelector('.product-badges__top-left');
    var existing = container ? container.querySelector('[data-outlet-badge]') : null;

    if (isOutlet) {
      if (existing) return;
      if (!container) {
        container = document.createElement('div');
        container.className = 'product-badges__top-left';
        media.insertBefore(container, media.firstChild);
      }
      var badge = document.createElement('span');
      badge.className = 'product-badge product-badge--outlet';
      badge.setAttribute('data-outlet-badge', '');
      badge.textContent = 'Outlet';
      container.insertBefore(badge, container.firstChild);
    } else if (existing) {
      existing.remove();
      if (container && !container.children.length) container.remove();
    }
  }

  /* ---------- always-on base card CSS ----------
     Three purposes:
       1. Hide Relewise's native favorite button — we render our own
          .wishlist-heart, so without this two hearts stack in the corner.
       2. Size the rating row + star SVG. The card snippet outputs an
          inline <svg> with only a viewBox; without explicit width/height
          the browser renders it at its 300×150 fallback box, which on
          PLP grid cards visually swallows the row. Sliders inject their
          own 13px star override later — source-order cascade wins.
       3. Wrap the hover size buttons at 8 per row on desktop and 6 per
          row on mobile. Without this the snippet renders every size into
          one cramped line; this matches the same caps applied in
          section-tabbed-collection-2xu.css for non-Relewise cards. */

  var BASE_CARD_CSS = [
    'relewise-product-favorite-button,',
    'relewise-favorite-button,',
    'relewise-product-tile-favorite-button,',
    '[part="favorite"],',
    '[part="favourite"] {',
    '  display: none !important;',
    '}',
    '.tabbed-product-card__rating {',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  gap: 4px !important;',
    /* line-height: 1 so the text glyph line box matches the icon box;
       otherwise the digits sit a fraction higher than the star. */
    '  line-height: 1 !important;',
    '}',
    '.tabbed-product-card__star {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  line-height: 1 !important;',
    '}',
    '.tabbed-product-card__star svg {',
    '  width: 14px !important;',
    '  height: 14px !important;',
    /* display: block removes the inline baseline gap. translateY shifts
       the star up half a pixel to compensate for the polygon\'s heavier
       bottom half — its visual centroid sits below the SVG box center. */
    '  display: block !important;',
    '  transform: translateY(-0.5px) !important;',
    '}',
    '.tabbed-product-card__sizes {',
    '  flex-wrap: wrap !important;',
    '}',
    '.tabbed-product-card__size-btn {',
    '  flex: 0 0 12.5% !important;',
    '  max-width: none !important;',
    '  border-radius: 0 !important;',
    '  margin-left: -1px !important;',
    '}',
    /* Brief feedback while the add-to-cart request is in flight, before
       the cart drawer slides open. */
    '.tabbed-product-card__size-btn--loading {',
    '  opacity: 0.55 !important;',
    '  pointer-events: none !important;',
    '  cursor: progress !important;',
    '}',
    '.tabbed-product-card__size-btn:first-child,',
    '.tabbed-product-card__size-btn:nth-child(8n+1) {',
    '  margin-left: 0 !important;',
    '  border-top-left-radius: var(--r-md, 8px) !important;',
    '  border-bottom-left-radius: var(--r-md, 8px) !important;',
    '}',
    '.tabbed-product-card__size-btn:nth-child(8n),',
    '.tabbed-product-card__size-btn:last-child {',
    '  border-top-right-radius: var(--r-md, 8px) !important;',
    '  border-bottom-right-radius: var(--r-md, 8px) !important;',
    '}',
    '@media (max-width: 749px) {',
    '  .tabbed-product-card__size-btn {',
    '    flex-basis: 16.6667% !important;',
    '  }',
    '  .tabbed-product-card__size-btn:nth-child(8n+1):not(:first-child):not(:nth-child(6n+1)) {',
    '    margin-left: -1px !important;',
    '    border-top-left-radius: 0 !important;',
    '    border-bottom-left-radius: 0 !important;',
    '  }',
    '  .tabbed-product-card__size-btn:nth-child(8n):not(:last-child):not(:nth-child(6n)) {',
    '    border-top-right-radius: 0 !important;',
    '    border-bottom-right-radius: 0 !important;',
    '  }',
    '  .tabbed-product-card__size-btn:nth-child(6n+1) {',
    '    margin-left: 0 !important;',
    '    border-top-left-radius: var(--r-md, 8px) !important;',
    '    border-bottom-left-radius: var(--r-md, 8px) !important;',
    '  }',
    '  .tabbed-product-card__size-btn:nth-child(6n) {',
    '    border-top-right-radius: var(--r-md, 8px) !important;',
    '    border-bottom-right-radius: var(--r-md, 8px) !important;',
    '  }',
    '}'
  ].join('\n');

  function injectBaseCardCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwBaseCardInjected) return;
    shadowRoot.__rwBaseCardInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(BASE_CARD_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-base-card', '');
    style.textContent = BASE_CARD_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- compact card overrides for the PDP upsell-v2 column ----------
     Cards rendered inside .pdp-upsell-v2 live in a narrow info-column slot,
     so we override the slider-tuned defaults from
     section-tabbed-collection-relewise.css that would otherwise force a
     ≥13rem min-width and 1.6rem title font on mobile (causing the grid to
     overflow the parent column). One rule kills swatches + rating together
     since both share the .tabbed-product-card__meta wrapper. */

  var UPSELL_V2_CARD_CSS = [
    '.tabbed-product-card__meta { display: none !important; }',
    /* Suppress the hover size-picker overlay in the PDP "Matcha med"
       upsell column. Sliders and the PLP grid keep theirs — this rule
       is scoped to .pdp-upsell-v2 shadow roots by injectUpsellV2Css. */
    '.tabbed-product-card__sizes { display: none !important; }',
    '.tabbed-product-card {',
    '  min-width: 0 !important;',
    '  flex: initial !important;',
    '}',
    '.tabbed-product-card__title {',
    '  font-size: 1.2rem !important;',
    '  font-weight: 500 !important;',
    '  line-height: 1.25 !important;',
    '  margin-bottom: 0.2rem !important;',
    '  overflow-wrap: anywhere;',
    '}',
    '.tabbed-product-card__price {',
    '  font-size: 1.1rem !important;',
    '}',
    '.tabbed-product-card__price--compare {',
    '  margin-right: 0.3rem !important;',
    '}',
    /* Step badge labels (BÄSTSÄLJARE, SÄLJER FORT, etc.) down a notch
       so they read as supporting metadata in the narrow info column. */
    '.product-badge {',
    '  font-size: 1rem !important;',
    '}'
  ].join('\n');

  function injectUpsellV2Css(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwUpsellV2Injected) return;
    shadowRoot.__rwUpsellV2Injected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(UPSELL_V2_CARD_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-upsell-v2', '');
    style.textContent = UPSELL_V2_CARD_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- slider tiles: shadow-DOM card overrides ----------
     Applies to every .relewise-slider-section card. These rules can't
     live in section-tabbed-collection-2xu.css because the slider tile
     renders its card markup inside <relewise-product-tile>'s shadow
     root — page-level stylesheets don't reach in. We inject them here
     so they take effect inside the tile.

     Always-on rules (all viewports):
       - rating: 12px, one step below the 14px title, so the score
         reads as supporting metadata
       - star icon: explicit 13x13 SVG sizing so the SVG inserted by
         the snippet renders at a sensible size even without
         font-size-driven sizing

     Mobile-only rules (<= 749px):
       - title 13px, price 12px (slightly smaller so the meta row
         doesn't crowd the image)
       - swatch row capped at 4: hide the 5th swatch onward. The
         snippet still renders up to 6 swatches plus a "+N" mobile
         indicator (now sized off products_count - 4)

     Loaded BEFORE the wide-slider override below so the wide variant
     can step the title down 1px more via source-order cascade. */

  var SLIDER_MOBILE_CARD_CSS = [
    /* Title weight: match the PLP grid (500) instead of inheriting the
       600 base. Applies at all viewports. */
    '.tabbed-product-card__title {',
    '  font-weight: 500 !important;',
    '}',
    '.tabbed-product-card__rating {',
    '  font-size: 12px !important;',
    '}',
    '.tabbed-product-card__star {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  line-height: 1 !important;',
    '}',
    '.tabbed-product-card__star svg {',
    '  width: 13px !important;',
    '  height: 13px !important;',
    '}',
    '@media (max-width: 749px) {',
    '  .tabbed-product-card__title {',
    '    font-size: 13px !important;',
    '  }',
    '  .tabbed-product-card__price {',
    '    font-size: 12px !important;',
    '  }',
    '  .tabbed-product-card__swatch-link:nth-child(n+5) {',
    '    display: none !important;',
    '  }',
    '}'
  ].join('\n');

  function injectSliderMobileCardCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwSliderMobileInjected) return;
    shadowRoot.__rwSliderMobileInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(SLIDER_MOBILE_CARD_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-slider-mobile', '');
    style.textContent = SLIDER_MOBILE_CARD_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- wide-slider variant: hide swatches + rating row + smaller title ----------
     Cards rendered inside .relewise-slider-section--wide (the "Nyligen
     visade produkter" slider) shouldn't show the swatches + score row
     because the denser 8.5-cards layout makes that secondary metadata
     too cramped to read. Same .tabbed-product-card__meta wrapper as the
     upsell-v2 case.

     The mobile @media block here steps the title 1px smaller than the
     standard slider rule (12px vs 13px) via source-order cascade —
     SLIDER_MOBILE_CARD_CSS is injected first and this rule overrides. */

  var WIDE_SLIDER_HIDE_META_CSS = [
    '.tabbed-product-card__meta { display: none !important; }',
    /* Suppress the hover size-picker overlay on the "Nyligen visade
       produkter" wide slider — scoped to .relewise-slider-section--wide
       shadow roots by injectWideSliderCss. Regular sliders and the PLP
       grid keep theirs. */
    '.tabbed-product-card__sizes { display: none !important; }',
    /* Title weight: match the PLP grid (500) instead of the 600 base.
       Applies at all viewports. */
    '.tabbed-product-card__title {',
    '  font-weight: 500 !important;',
    '}',
    /* Price reads as supporting metadata on the wide slider: drop a
       step in size and shift to --ink-2 (a touch lighter than the
       title's --ink). Custom properties inherit through the shadow
       boundary; fallback covers any host where --ink-2 isn't set. */
    '.tabbed-product-card__price {',
    '  font-size: 12px !important;',
    '  color: var(--ink-2, #666666) !important;',
    '}',
    '@media (max-width: 749px) {',
    '  .tabbed-product-card__title {',
    '    font-size: 12px !important;',
    '  }',
    '}'
  ].join('\n');

  function injectWideSliderCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwWideSliderInjected) return;
    shadowRoot.__rwWideSliderInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(WIDE_SLIDER_HIDE_META_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-wide-slider', '');
    style.textContent = WIDE_SLIDER_HIDE_META_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- PLP grid: viewport-specific card overrides ----------
     Cards inside a <relewise-product-search> render two-up on mobile
     and need slightly smaller title/price/rating text than the slider
     defaults so the meta row doesn't crowd the image. Slider hosts get
     their own larger overrides via SLIDER_MOBILE_CARD_CSS — the slider
     injection runs AFTER this one in walk(), so cascade preserves
     slider sizing where it applies.

     Desktop block lightens the title and steps title/price/rating down
     a notch from the regular stylesheet defaults (14/13/12) plus adds
     16px breathing room below each card. */

  var PLP_GRID_CSS = [
    '@media (max-width: 749px) {',
    '  .tabbed-product-card__title {',
    '    font-size: 11px !important;',
    '    font-weight: 500 !important;',
    '  }',
    '  .tabbed-product-card__price {',
    '    font-size: 10px !important;',
    '  }',
    '  .tabbed-product-card__rating {',
    '    font-size: 11px !important;',
    '  }',
    '  .tabbed-product-card__star svg {',
    '    width: 12px !important;',
    '    height: 12px !important;',
    '  }',
    '  .tabbed-product-card__swatch {',
    '    width: 14px !important;',
    '    height: 14px !important;',
    '  }',
    /* Cap visible swatches at 4 on mobile. The snippet renders up to 6
       and only emits a "+N" indicator when count > 4 (sized from
       products_count - 4), so hiding the 5th onward lines up with that
       math. Belt-and-suspenders display rules for the desktop/mobile
       indicators in case the regular stylesheet doesn't reach inside
       this shadow root. */
    '  .tabbed-product-card__swatch-link:nth-child(n+5) {',
    '    display: none !important;',
    '  }',
    '  .tabbed-product-card__swatch-more--desktop {',
    '    display: none !important;',
    '  }',
    '  .tabbed-product-card__swatch-more--mobile {',
    '    display: inline !important;',
    '    font-size: 9px !important;',
    '    margin-left: 0 !important;',
    '  }',
    /* Badge pills (campaign/editorial/discount labels overlaying the
       product image). Regular stylesheet sets 10px/500 with discount
       at 600 — step down a notch so the badges read as supporting
       metadata on the smaller mobile cards. */
    '  .product-badge {',
    '    font-size: 9px !important;',
    '    font-weight: 400 !important;',
    '  }',
    '  .product-badge--discount {',
    '    font-weight: 500 !important;',
    '  }',
    '}',
    '@media (min-width: 750px) {',
    '  .tabbed-product-card__title {',
    '    font-size: 13px !important;',
    '    font-weight: 500 !important;',
    '  }',
    '  .tabbed-product-card__price {',
    '    font-size: 12px !important;',
    '  }',
    '  .tabbed-product-card__rating {',
    '    font-size: 11px !important;',
    '  }',
    '  .tabbed-product-card {',
    '    padding-bottom: 16px !important;',
    '  }',
    '}'
  ].join('\n');

  function injectPlpGridCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwPlpGridInjected) return;
    shadowRoot.__rwPlpGridInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(PLP_GRID_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-plp-grid', '');
    style.textContent = PLP_GRID_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- PLP results grid: tighter gap + equal columns on mobile ----
     <relewise-product-search-results> sets its own display:grid +
     column-gap on the host via a :host rule inside its shadow. Document
     CSS can't beat a shadow :host rule, so we inject a :host override
     directly into that shadow root.

     Mobile: Relewise's default grid-template-columns resolves to
     unequal track widths (observed ~173.9px / ~135.4px = ~56% / ~44%),
     so pin both tracks to minmax(0, 1fr). The minmax(0, ...) part is
     required to prevent long product titles from pushing one track
     wider than its share.

     Desktop: drop the default 15px column gap to 8px. */

  var PLP_RESULTS_GAP_CSS = [
    '@media (max-width: 749px) {',
    '  :host {',
    '    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;',
    '    column-gap: 4px !important;',
    '  }',
    '}',
    '@media (min-width: 750px) {',
    '  :host {',
    '    column-gap: 8px !important;',
    '  }',
    '}'
  ].join('\n');

  function injectPlpResultsGapCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwPlpResultsGapInjected) return;
    shadowRoot.__rwPlpResultsGapInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(PLP_RESULTS_GAP_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-plp-results-gap', '');
    style.textContent = PLP_RESULTS_GAP_CSS;
    shadowRoot.appendChild(style);
  }

  /* ---------- cart drawer: compact card overrides ----------
     Cards rendered inside the cart drawer's Relewise "Purchased With
     Cart" recommendation (<relewise-purchased-with-multiple-products
     class="cart-drawer__relewise-recs">) sit three-up in a ~400px
     drawer, so they need to lose all secondary metadata and shrink.
     Scoped to .cart-drawer__relewise-recs so no other Relewise surface
     is affected.

       - hide swatch + rating row (shared .tabbed-product-card__meta
         wrapper) and the hover size-picker — not useful at this size
       - hide the wishlist heart and the image badges/labels
         (product-badges__top-*) — too cramped to be useful here
       - min-width: 0 so three cards fit the row (paired with the
         minmax(0, 1fr) grid on the host in component-cart-drawer.css)
       - smaller, lighter title and smaller price */

  var CART_DRAWER_CARD_CSS = [
    '.tabbed-product-card__meta { display: none !important; }',
    '.tabbed-product-card__sizes { display: none !important; }',
    '.wishlist-heart { display: none !important; }',
    '.product-badges__top-left,',
    '.product-badges__top-right { display: none !important; }',
    '.tabbed-product-card {',
    '  min-width: 0 !important;',
    '  flex: initial !important;',
    '}',
    '.tabbed-product-card__title {',
    '  font-size: 11px !important;',
    '  font-weight: 400 !important;',
    '  line-height: 1.25 !important;',
    '  margin-bottom: 0.2rem !important;',
    '  overflow-wrap: anywhere;',
    '}',
    '.tabbed-product-card__price {',
    '  font-size: 10px !important;',
    '}'
  ].join('\n');

  function injectCartDrawerCss(shadowRoot) {
    if (!shadowRoot || shadowRoot.__rwCartDrawerInjected) return;
    shadowRoot.__rwCartDrawerInjected = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(CART_DRAWER_CARD_CSS);
      shadowRoot.adoptedStyleSheets = (shadowRoot.adoptedStyleSheets || []).concat([sheet]);
      return;
    } catch (_) { /* fall through to <style> tag */ }
    var style = document.createElement('style');
    style.setAttribute('data-rw-cart-drawer', '');
    style.textContent = CART_DRAWER_CARD_CSS;
    shadowRoot.appendChild(style);
  }

  // Walk outward across shadow boundaries (closest() can't pierce them).
  function isHostInUpsellV2(host) {
    var node = host;
    while (node) {
      if (node.closest && node.closest('.pdp-upsell-v2')) return true;
      var root = node.getRootNode && node.getRootNode();
      if (!root || root === document || !root.host) return false;
      node = root.host;
    }
    return false;
  }

  function isHostInWideSlider(host) {
    var node = host;
    while (node) {
      if (node.closest && node.closest('.relewise-slider-section--wide')) return true;
      var root = node.getRootNode && node.getRootNode();
      if (!root || root === document || !root.host) return false;
      node = root.host;
    }
    return false;
  }

  function isHostInSlider(host) {
    var node = host;
    while (node) {
      if (node.closest && node.closest('.relewise-slider-section')) return true;
      var root = node.getRootNode && node.getRootNode();
      if (!root || root === document || !root.host) return false;
      node = root.host;
    }
    return false;
  }

  // PLP grid = any card descendant of a <relewise-product-search>
  // element (covers both the test2 PLP and /search). Slider/upsell
  // hosts are checked separately and override via cascade order.
  function isHostInPlpGrid(host) {
    var node = host;
    while (node) {
      if (node.closest && node.closest('relewise-product-search')) return true;
      var root = node.getRootNode && node.getRootNode();
      if (!root || root === document || !root.host) return false;
      node = root.host;
    }
    return false;
  }

  // Cart drawer = any card descendant of the Relewise "Purchased With
  // Cart" recommendation host (.cart-drawer__relewise-recs).
  function isHostInCartDrawer(host) {
    var node = host;
    while (node) {
      if (node.closest && node.closest('.cart-drawer__relewise-recs')) return true;
      var root = node.getRootNode && node.getRootNode();
      if (!root || root === document || !root.host) return false;
      node = root.host;
    }
    return false;
  }

  /* ---------- shadow-root walker ---------- */

  // Walk recursively because Relewise nests shadows several layers deep
  // (<relewise-product-search> → <relewise-product-search-results> → <relewise-product-tile>).
  // Attach a MutationObserver to each shadow root so shadows populated later
  // (load-more, recommendation widgets) get the favorite-hide stylesheet too.
  function walk(root) {
    if (!root) return;
    var hosts = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (host.shadowRoot) {
        injectBaseCardCss(host.shadowRoot);
        // Tighter column gap on mobile for the products grid host.
        if (host.tagName === 'RELEWISE-PRODUCT-SEARCH-RESULTS') {
          injectPlpResultsGapCss(host.shadowRoot);
        }
        // PLP/search card typography (must come BEFORE slider/upsell
        // overrides so those win the cascade where they apply).
        if (isHostInPlpGrid(host) && !isHostInSlider(host) && !isHostInUpsellV2(host)) {
          injectPlpGridCss(host.shadowRoot);
        }
        if (isHostInUpsellV2(host)) {
          injectUpsellV2Css(host.shadowRoot);
        }
        if (isHostInSlider(host)) {
          injectSliderMobileCardCss(host.shadowRoot);
        }
        if (isHostInWideSlider(host)) {
          injectWideSliderCss(host.shadowRoot);
        }
        if (isHostInCartDrawer(host)) {
          injectCartDrawerCss(host.shadowRoot);
        }
        if (!host.__rwShadowObserved) {
          host.__rwShadowObserved = true;
          try {
            new MutationObserver(scheduleWalk).observe(host.shadowRoot, {
              childList: true,
              subtree: true
            });
          } catch (_) { /* ignore */ }
        }
        walk(host.shadowRoot);
      }
    }
  }

  var scheduled = false;
  function scheduleWalk() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      walk(document);
    });
  }

  function bootstrap() {
    walk(document);
    new MutationObserver(scheduleWalk).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    // Some tiles populate asynchronously after first paint — re-walk a few
    // times to catch them without resorting to long-running polling.
    setTimeout(function () { walk(document); }, 250);
    setTimeout(function () { walk(document); }, 1000);
    setTimeout(function () { walk(document); }, 2500);
  }

  if (document.readyState !== 'loading') {
    bootstrap();
  } else {
    document.addEventListener('DOMContentLoaded', bootstrap);
  }
})();
